import { CARD_INNER_PADDING } from "../../../../constants/constants";
import {
  useGetPageFromPath,
  useProcessesInVisibleGroups,
} from "../../../../utils/customHooks/genericHooks";

import { DASHBOARD_CARD_PROPS } from "../dashboardPagesContentUtils";
import { darkModeBackgroundHoveredColor } from "constants/colors";
import { useIsDarkMode } from "store/atoms";
import { css } from "styled-components/macro";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import ErrorBoundary from "components/SimpleComponents/Errors/ErrorBoundary";
import BtnToggleTilesFullDay from "../../BtnToggleTilesFullDay";
import CardWithControls from "components/Dashboard/CardWithControls/CardWithControls";
import GroupMetrics from "components/Dashboard/GroupMetrics/GroupMetrics";
import OverallBarstack from "components/Dashboard/OverallBarstack/OverallBarstack";
import { useMemo } from "react";
import { Badge, Box, Tooltip, Typography } from "@mui/material";
import {
  LINE_ROLE_ICONS,
  useLeaderConstraintProcessIds,
} from "components/Dashboard/Tiles/ProcessTile/LineRoleDecorator";
import styled from "styled-components/macro";
import {
  lightAccentSuccess10,
  lightAccentSuccess50,
  lightAccentSuccess90,
} from "constants/colors";

const ProcessIndicatorContainer = styled(Box)`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 8px;
  transform: scale(0.8);
  transform-origin: top right;
  .MuiBadge-badge {
    top: 10px !important;
    left: -10px !important;
    svg {
      transform: scale(0.8);
      color: ${lightAccentSuccess90};
      background: ${lightAccentSuccess10};
      border: 1px solid ${lightAccentSuccess50};
      border-radius: 4px;
    }
  }

  .process-indicator {
    display: flex;
    align-items: center;
    margin-left: 12px;
  }
`;

export function GroupSummaryCard() {
  const {
    isGroupTilesPage,
    isTimelinePage,
    isProcessTilesPage,
    isAllWorkSchedulesPage,
  } = useGetPageFromPath();

  const isGroupOrProcessTilesPage = isGroupTilesPage || isProcessTilesPage;
  const [isDarkMode] = useIsDarkMode();

  const { translate } = useFlightTranslation();
  const showButterfly = useMemo(
    () =>
      !window.location.search.includes("hideSummary=true") &&
      !(isAllWorkSchedulesPage && isGroupTilesPage),
    [isAllWorkSchedulesPage, isGroupTilesPage]
  );

  // Get the leader/constraint process IDs
  const { leaderProcessId, constraintProcessIds } =
    useLeaderConstraintProcessIds();

  // Get all processes to find names by ID
  const allProcesses = useProcessesInVisibleGroups();

  // Find leader and constraint processes
  const leaderProcess = useMemo(() => {
    return leaderProcessId
      ? allProcesses.find(p => p.id === leaderProcessId)
      : null;
  }, [leaderProcessId, allProcesses]);

  const constraintProcesses = useMemo(() => {
    if (!constraintProcessIds?.length) {
      return [];
    }
    return allProcesses.filter(p => constraintProcessIds.includes(p.id));
  }, [constraintProcessIds, allProcesses]);

  // Check if there is a leader or constraint process
  const hasLeader = Boolean(leaderProcess);
  const hasConstraint = Boolean(constraintProcesses.length);
  return (
    <CardWithControls
      {...DASHBOARD_CARD_PROPS}
      id="GroupSummaryCard"
      className="Card_GroupSummary"
      title={isTimelinePage ? translate("Group_Summary") : ""}
      customCss={css`
        position: relative;
        ${isGroupOrProcessTilesPage
          ? css`
              &&& {
                margin-top: 10px;
              }
              .cardContent {
                padding-bottom: 48px;
              }
            `
          : ``}
        .cardContent {
          padding: ${0}px 0 ${2 * CARD_INNER_PADDING}px;
        }
        [class*="CardTopbarStyles"] {
          position: relative;
          z-index: 2;
          &:hover {
            background: ${isDarkMode
              ? darkModeBackgroundHoveredColor
              : "hsl(0,0%,90%)"};
          }
        }
        @media print {
          &&& {
            position: relative !important;
          }
        }
      `}
    >
      {/* Leader/Constraint process indicators */}
      {(hasLeader || hasConstraint) && (
        <ProcessIndicatorContainer>
          {hasLeader && leaderProcess && (
            <div className="process-indicator">
              <Tooltip title={translate("Leader_Process")} disableInteractive>
                <Badge
                  badgeContent={LINE_ROLE_ICONS.LEADER}
                  anchorOrigin={{ horizontal: "left", vertical: "top" }}
                  className="machineNameContainerWithBadge"
                >
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{ fontWeight: 500, pl: 1 }}
                  >
                    {leaderProcess.name}
                  </Typography>
                </Badge>
              </Tooltip>
            </div>
          )}
          {hasConstraint &&
            constraintProcesses.map(process => (
              <div
                className="process-indicator"
                key={`constraint-${process.id}`}
              >
                <Tooltip
                  title={translate("Constraint_Process")}
                  disableInteractive
                >
                  <Badge
                    badgeContent={LINE_ROLE_ICONS.CONSTRAINT}
                    anchorOrigin={{ horizontal: "left", vertical: "top" }}
                    className="machineNameContainerWithBadge"
                  >
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{ fontWeight: 500, pl: 1 }}
                    >
                      {process.name}
                    </Typography>
                  </Badge>
                </Tooltip>
              </div>
            ))}
        </ProcessIndicatorContainer>
      )}

      {isGroupOrProcessTilesPage && <BtnToggleTilesFullDay />}

      <ErrorBoundary small={true} id="GroupMetrics">
        <GroupMetrics />
      </ErrorBoundary>
      <ErrorBoundary small={true} id="OverallBarstack">
        {showButterfly ? <OverallBarstack /> : null}
      </ErrorBoundary>
    </CardWithControls>
  );
}
